import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

interface ChatPanelStateContextType {
  isChatMinimized: boolean;
  isChatExpanded: boolean;
  toggleChatPanel: () => void;
  toggleChatExpanded: () => void;
}

const ChatPanelStateContext = createContext<ChatPanelStateContextType>({
  isChatMinimized: false,
  isChatExpanded: false,
  toggleChatPanel: () => {},
  toggleChatExpanded: () => {},
});

export const useChatPanelState = () => useContext(ChatPanelStateContext);

interface ChatPanelStateProviderProps {
  children: React.ReactNode;
}

export const ChatPanelStateProvider: React.FC<ChatPanelStateProviderProps> = ({ children }) => {
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [isChatExpanded, setIsChatExpanded] = useState(false);

  const toggleChatPanel = useCallback(() => {
    setIsChatMinimized((prev) => !prev);
    if (isChatExpanded) setIsChatExpanded(false);
  }, [isChatExpanded]);

  const toggleChatExpanded = useCallback(() => {
    setIsChatExpanded((prev) => !prev);
    if (isChatMinimized) setIsChatMinimized(false);
  }, [isChatMinimized]);

  const value = useMemo(() => ({
    isChatMinimized,
    isChatExpanded,
    toggleChatPanel,
    toggleChatExpanded,
  }), [isChatMinimized, isChatExpanded, toggleChatPanel, toggleChatExpanded]);

  return (
    <ChatPanelStateContext.Provider value={value}>
      {children}
    </ChatPanelStateContext.Provider>
  );
};
